import React, { useEffect, useState } from "react";
import { AdminLayout } from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import * as Lucide from "lucide-react";
import { toast } from "sonner";

const PROVIDERS = [
  {
    id: "cashfree",
    name: "Cashfree Payments",
    desc: "UPI Collect, Payment Links, Webhooks. Most popular Indian gateway.",
    docs_url: "https://docs.cashfree.com/docs/getting-started",
    color: "from-green-500/20 to-emerald-500/10 border-green-500/30",
    icon: Lucide.Wallet,
  },
  {
    id: "razorpay",
    name: "Razorpay",
    desc: "UPI + Cards + Wallets. Broadest payment mix; standard checkout JS.",
    docs_url: "https://razorpay.com/docs/",
    color: "from-blue-500/20 to-indigo-500/10 border-blue-500/30",
    icon: Lucide.CreditCard,
  },
  {
    id: "decentro",
    name: "Decentro",
    desc: "Banking-API first. UPI collect, virtual accounts, refunds via deep ICICI rails.",
    docs_url: "https://docs.decentro.tech/",
    color: "from-purple-500/20 to-fuchsia-500/10 border-purple-500/30",
    icon: Lucide.Building2,
  },
];

export default function AdminAggregators() {
  const [data, setData] = useState({ active: null, providers: [] });
  const [editing, setEditing] = useState(null);
  const [orders, setOrders] = useState([]);
  const [events, setEvents] = useState([]);

  const load = async () => {
    const r = await api.get("/admin/aggregators");
    setData(r.data);
    const o = await api.get("/admin/aggregators/orders").catch(() => ({ data: [] }));
    setOrders(o.data || []);
    const e = await api.get("/admin/aggregators/events").catch(() => ({ data: [] }));
    setEvents(e.data || []);
  };
  useEffect(() => { load(); }, []);

  const save = async (cfg) => {
    try {
      await api.put("/admin/aggregators", cfg);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const setActive = async (provider) => {
    try {
      await api.post("/admin/aggregators/active", { provider });
      toast.success(`${provider} is now active`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const test = async (provider) => {
    try {
      const r = await api.post("/admin/aggregators/test", { provider });
      toast.success(r.data.ok ? `${provider}: keys present (stub)` : `${provider}: ${r.data.reason}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-7" data-testid="admin-aggregators">
        <header>
          <h1 className="bx-heading text-3xl font-extrabold">Payment aggregators</h1>
          <p className="text-slate-400 mt-1">Switchable UPI / payment aggregators. Configure keys, pick the active provider, and route customer orders through it.</p>
          <div className="mt-3 inline-flex items-center gap-2 text-xs text-slate-300 bg-[#0B0B14] border border-violet-500/30 rounded-full px-3 py-1">
            <Lucide.Activity className="w-3.5 h-3.5 text-violet-400" />
            Active provider:
            <span className="font-bold text-violet-300">{data.active || "— none —"}</span>
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {PROVIDERS.map((p) => {
            const cfg = data.providers.find((x) => x.provider === p.id) || {};
            const isActive = data.active === p.id;
            const Icon = p.icon;
            return (
              <div key={p.id} className={`bx-card p-5 bg-gradient-to-br ${p.color}`} data-testid={`agg-card-${p.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#0B0B14] border border-white/10 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <div className="bx-heading font-bold text-lg">{p.name}</div>
                      <div className="text-xs text-slate-300/70">{p.desc}</div>
                    </div>
                  </div>
                  <span className={`text-[10px] font-bold px-2 py-1 rounded-full border ${cfg.enabled ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-slate-500/15 text-slate-400 border-slate-500/30"}`}>
                    {cfg.enabled ? "ENABLED" : "DISABLED"}
                  </span>
                </div>

                <div className="mt-4 space-y-1.5 text-xs">
                  <div className="flex justify-between"><span className="text-slate-400">Mode</span><span className="text-white font-medium">{cfg.mode || "—"}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Key ID</span><span className="text-white font-mono">{cfg.key_id_masked || "—"}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Secret</span><span className="text-white font-mono">{cfg.key_secret_masked || "—"}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Webhook</span><span className="text-white font-mono">{cfg.webhook_secret_masked || "—"}</span></div>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  <button data-testid={`agg-edit-${p.id}`} onClick={() => setEditing({ ...cfg, provider: p.id })} className="bx-btn-ghost text-xs"><Lucide.Pencil className="w-3.5 h-3.5 mr-1" /> Edit keys</button>
                  <button data-testid={`agg-test-${p.id}`} onClick={() => test(p.id)} className="bx-btn-ghost text-xs"><Lucide.Zap className="w-3.5 h-3.5 mr-1" /> Test</button>
                  {!isActive && cfg.enabled && (
                    <button data-testid={`agg-activate-${p.id}`} onClick={() => setActive(p.id)} className="bx-btn-primary text-xs">Set active</button>
                  )}
                  {isActive && (
                    <span className="text-xs text-emerald-300 inline-flex items-center gap-1"><Lucide.CheckCircle2 className="w-3.5 h-3.5" /> ACTIVE</span>
                  )}
                  <a href={p.docs_url} target="_blank" rel="noreferrer" className="text-xs text-violet-400 underline ml-auto">Docs ↗</a>
                </div>
              </div>
            );
          })}
        </div>

        {editing && <EditorModal initial={editing} onClose={() => setEditing(null)} onSave={save} />}

        <section>
          <div className="flex items-center justify-between mb-2">
            <h2 className="bx-heading text-xl font-bold">Recent orders</h2>
            <button onClick={load} className="bx-btn-ghost text-xs"><Lucide.RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh</button>
          </div>
          <div className="bx-card overflow-x-auto">
            {orders.length === 0 ? (
              <div className="p-6 text-center text-slate-500 text-sm">No orders yet. Build a checkout that calls <code className="text-violet-400">POST /api/aggregator/create-order</code>.</div>
            ) : (
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-slate-400 border-b border-white/5">
                  <th className="px-4 py-2">When</th><th>Ref</th><th>Provider</th><th>Customer</th><th>Amount</th><th>Status</th>
                </tr></thead>
                <tbody>
                  {orders.map((o) => (
                    <tr key={o.id} className="border-b border-white/5">
                      <td className="px-4 py-2 text-xs text-slate-400">{(o.created_at || "").slice(0, 19).replace("T", " ")}</td>
                      <td className="font-mono text-xs text-violet-300">{o.merchant_reference_id}</td>
                      <td className="text-white">{o.provider}</td>
                      <td className="text-slate-300">{o.customer_name || "—"}</td>
                      <td className="text-white font-mono">{o.currency === "INR" ? "₹" : "$"}{Number(o.amount).toFixed(2)}</td>
                      <td><span className="text-xs px-2 py-0.5 rounded-full bg-slate-500/15 text-slate-300 border border-slate-500/30">{o.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>

        <section>
          <h2 className="bx-heading text-xl font-bold mb-2">Recent webhook events</h2>
          <div className="bx-card overflow-x-auto">
            {events.length === 0 ? (
              <div className="p-6 text-center text-slate-500 text-sm">No webhook events received yet.</div>
            ) : (
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-slate-400 border-b border-white/5">
                  <th className="px-4 py-2">When</th><th>Provider</th><th>Ref</th><th>Status</th><th>Amount</th>
                </tr></thead>
                <tbody>
                  {events.map((e) => (
                    <tr key={e.id} className="border-b border-white/5">
                      <td className="px-4 py-2 text-xs text-slate-400">{(e.received_at || "").slice(0, 19).replace("T", " ")}</td>
                      <td className="text-white">{e.provider}</td>
                      <td className="font-mono text-xs text-violet-300">{e.merchant_reference_id || "—"}</td>
                      <td className="text-slate-300">{e.status || "—"}</td>
                      <td className="text-white font-mono">{e.amount ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>
      </div>
    </AdminLayout>
  );
}

function EditorModal({ initial, onClose, onSave }) {
  const [cfg, setCfg] = useState({
    provider: initial.provider,
    mode: initial.mode || "sandbox",
    enabled: !!initial.enabled,
    key_id: "",
    key_secret: "",
    webhook_secret: "",
    merchant_id: initial.merchant_id || "",
    notes: initial.notes || "",
  });

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-5">
      <div className="bx-card p-6 max-w-lg w-full">
        <div className="bx-heading text-lg font-bold capitalize">Configure {initial.provider}</div>
        <div className="mt-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400">Mode</label>
              <select className="bx-input mt-1" value={cfg.mode} onChange={(e) => setCfg({ ...cfg, mode: e.target.value })}>
                <option value="sandbox">Sandbox</option>
                <option value="live">Live</option>
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={cfg.enabled} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} />
                <span className="text-slate-300">Enabled</span>
              </label>
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-400">Key ID / App ID</label>
            <input className="bx-input bx-mono mt-1" value={cfg.key_id} onChange={(e) => setCfg({ ...cfg, key_id: e.target.value })} placeholder={initial.key_id_masked ? `current: ${initial.key_id_masked} (leave blank to keep)` : "paste your key id"} />
          </div>
          <div>
            <label className="text-xs text-slate-400">Secret Key</label>
            <input type="password" className="bx-input bx-mono mt-1" value={cfg.key_secret} onChange={(e) => setCfg({ ...cfg, key_secret: e.target.value })} placeholder={initial.key_secret_masked ? `current: ${initial.key_secret_masked} (leave blank to keep)` : "paste your secret"} />
          </div>
          <div>
            <label className="text-xs text-slate-400">Webhook secret</label>
            <input type="password" className="bx-input bx-mono mt-1" value={cfg.webhook_secret} onChange={(e) => setCfg({ ...cfg, webhook_secret: e.target.value })} placeholder={initial.webhook_secret_masked ? `current: ${initial.webhook_secret_masked} (leave blank to keep)` : "HMAC secret"} />
          </div>
          <div>
            <label className="text-xs text-slate-400">Merchant ID (optional)</label>
            <input className="bx-input mt-1" value={cfg.merchant_id} onChange={(e) => setCfg({ ...cfg, merchant_id: e.target.value })} />
          </div>
          <div>
            <label className="text-xs text-slate-400">Notes</label>
            <textarea className="bx-input mt-1" rows={2} value={cfg.notes} onChange={(e) => setCfg({ ...cfg, notes: e.target.value })} />
          </div>
          <div className="text-xs text-slate-500 bg-[#0B0B14] border border-white/5 rounded-md p-2">
            <Lucide.Info className="w-3.5 h-3.5 inline mr-1 text-violet-400" />
            Webhook URL for this provider: <code className="text-violet-300">/api/aggregator/webhook/{initial.provider}</code> (HMAC-SHA256 of raw body in <code>X-Webhook-Signature</code>).
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="bx-btn-ghost">Cancel</button>
          <button data-testid="agg-modal-save" onClick={() => onSave(cfg)} className="bx-btn-primary">Save</button>
        </div>
      </div>
    </div>
  );
}
